import {
  faXmark,
  faHouseBlank,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";
import { faUsers } from "@fortawesome/pro-light-svg-icons";
import { GOAL_DRAWER_WIDTH } from "../../constants";
import Button from "app/storybookComponents/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "app/shadcnComponents/ui/drawer";
import { Separator } from "app/shadcnComponents/ui/separator";
import { CustomVisibilityEntities } from "../../types";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { useAppSelector } from "utils/redux/hooks";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import { getEntries, getS, getUserName } from "utils/helperFunctions";
import { DrawerSearchInput } from "./DrawerSearchInput";
import { cn } from "app/lib/utils";
import AvatarCircle from "app/components/AvatarCircle";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (customVisibilityEntities: CustomVisibilityEntities) => void;
  preselectedCustomVisibilityEntities?: CustomVisibilityEntities;
  selectableEntities?: {
    userAccountIds?: number[];
    teamIds?: number[];
    departmentIds?: number[];
  };
  excludedSelectableEntities?: {
    userAccountIds?: number[];
    teamIds?: number[];
    departmentIds?: number[];
  };
}

const normalize = (v?: string) =>
  v?.toLowerCase().replaceAll(/\s+/g, " ").trim() ?? "";

const getSelectedEntitiesMapsFromProps = (
  preselectedCustomVisibilityEntities?: CustomVisibilityEntities
) => {
  const incomingEntityMap: Record<
    keyof CustomVisibilityEntities,
    Record<number, boolean>
  > = {
    userAccountIds: {},
    teamIds: {},
    departmentIds: {},
  };
  if (!preselectedCustomVisibilityEntities) {
    return incomingEntityMap;
  }

  getEntries(preselectedCustomVisibilityEntities).forEach(
    ([entityType, entityIds]) => {
      const typedEntityType = entityType as keyof CustomVisibilityEntities;
      entityIds.forEach((entityId) => {
        incomingEntityMap[typedEntityType][entityId] = true;
      });
    }
  );
  return incomingEntityMap;
};

const EntitySelectorDrawer = ({
  isOpen,
  onClose,
  onSave,
  preselectedCustomVisibilityEntities,
  selectableEntities,
  excludedSelectableEntities,
}: Props) => {
  // ---------------------- Redux Selectors ----------------------
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const departments = useAppSelector(selectDepartments);
  const teamsById = useAppSelector(selectTeamsByTeamId);

  // ---------------------- States ----------------------
  const [searchInput, setSearchInput] = useState("");
  const [selectedEntitiesMaps, setSelectedEntitiesMaps] = useState<
    Record<keyof CustomVisibilityEntities, Record<number, boolean>>
  >({
    userAccountIds: {},
    teamIds: {},
    departmentIds: {},
  });

  // ---------------------- Memoized Values ----------------------
  const getFilteredEntityIds = useCallback(
    (allIds: number[], selectableIds?: number[], excludedIds?: number[]) => {
      const ids = selectableIds ?? allIds;
      return ids.filter((id) => !excludedIds?.includes(id));
    },
    []
  );

  const teamIdsToUse = useMemo(
    () =>
      getFilteredEntityIds(
        Object.keys(teamsById).map(Number),
        selectableEntities?.teamIds,
        excludedSelectableEntities?.teamIds
      ),
    [
      selectableEntities?.teamIds,
      excludedSelectableEntities?.teamIds,
      teamsById,
      getFilteredEntityIds,
    ]
  );

  const departmentIdsToUse = useMemo(
    () =>
      getFilteredEntityIds(
        Object.keys(departments)
          .map(Number)
          .filter((id) => departments[id].companyAccountId !== 0), // Filter out all sample departments
        selectableEntities?.departmentIds,
        excludedSelectableEntities?.departmentIds
      ),
    [
      selectableEntities?.departmentIds,
      excludedSelectableEntities?.departmentIds,
      departments,
      getFilteredEntityIds,
    ]
  );

  const userAccountIdsToUse = useMemo(
    () =>
      getFilteredEntityIds(
        Object.keys(usersById).map(Number),
        selectableEntities?.userAccountIds,
        excludedSelectableEntities?.userAccountIds
      ),
    [
      selectableEntities?.userAccountIds,
      excludedSelectableEntities?.userAccountIds,
      usersById,
      getFilteredEntityIds,
    ]
  );

  const filteredTeams = useMemo(() => {
    const query = normalize(searchInput);
    return teamIdsToUse
      .map((teamId) => teamsById[teamId])
      .filter(
        (team) => team && (!query || normalize(team.teamName).includes(query))
      );
  }, [teamIdsToUse, teamsById, searchInput]);

  const filteredDepartments = useMemo(() => {
    const query = normalize(searchInput);
    return departmentIdsToUse
      .map((deptId) => departments[deptId])
      .filter(
        (dept) => dept && (!query || normalize(dept.name).includes(query))
      );
  }, [departmentIdsToUse, departments, searchInput]);

  const filteredUsers = useMemo(() => {
    const query = normalize(searchInput);
    return userAccountIdsToUse
      .map((userId) => usersById[userId])
      .filter(
        (user) =>
          user &&
          (!query ||
            normalize(getUserName(user)).includes(query) ||
            normalize(user.emailAddress).includes(query) ||
            normalize(user.jobTitle).includes(query))
      );
  }, [userAccountIdsToUse, usersById, searchInput]);

  // ---------------------- Effects ----------------------
  useEffect(() => {
    setSelectedEntitiesMaps(
      getSelectedEntitiesMapsFromProps(preselectedCustomVisibilityEntities)
    );
  }, [preselectedCustomVisibilityEntities]);

  // ---------------------- Handlers ----------------------
  const onClickHandler = (
    entityType: keyof CustomVisibilityEntities,
    entityId: number
  ) => {
    setSelectedEntitiesMaps((prev) => {
      return {
        ...prev,
        [entityType]: {
          ...prev[entityType],
          [entityId]: !prev[entityType][entityId],
        },
      };
    });
  };

  const onSaveHandler = () => {
    const customVisibilityEntities: CustomVisibilityEntities = {
      teamIds: [],
      userAccountIds: [],
      departmentIds: [],
    };

    Object.entries(selectedEntitiesMaps).forEach(([entityType, entityMap]) => {
      const typedEntityType = entityType as keyof CustomVisibilityEntities;
      Object.entries(entityMap).forEach(([entityId, isSelected]) => {
        if (isSelected) {
          customVisibilityEntities[typedEntityType].push(Number(entityId));
        }
      });
    });
    onSave(customVisibilityEntities);
    onClose();
  };

  const onCloseDrawer = () => {
    setSelectedEntitiesMaps(
      getSelectedEntitiesMapsFromProps(preselectedCustomVisibilityEntities)
    );
    onClose();
  };

  // ---------------------- Renders ----------------------
  const getSelectItem = (
    entityId: number,
    entityType: keyof CustomVisibilityEntities,
    title: string,
    description: string,
    avatarCircle?: JSX.Element | null
  ) => {
    const isSelected = selectedEntitiesMaps[entityType][entityId];
    const selectorClassName = cn(
      "d-flex justify-content-between w-full px-3 py-2 hover:bg-[var(--surface-2)]",
      isSelected && "bg-[var(--interactive-bg)]"
    );

    return (
      <button
        key={`${entityType}_${entityId}`}
        onClick={() => onClickHandler(entityType, entityId)}
        className={selectorClassName}
      >
        <div className="d-flex align-items-center gap-2">
          {avatarCircle ? <div>{avatarCircle}</div> : null}
          <div className="d-flex align-items-start flex-col">
            <p className="text-[color:var(--text-primary)] text-[length:var(--fontSize-label)]">
              {title}
            </p>
            <p className="text-[color:var(--text-secondary)] text-[length:var(--fontSize-micro)]">
              <span>{description}</span>
            </p>
          </div>
        </div>
        <input type="checkbox" checked={isSelected} onChange={() => {}} />
      </button>
    );
  };

  const getTeamSelection = () => {
    if (filteredTeams.length === 0) {
      return null;
    }
    return (
      <>
        <div className={labelClassName}>
          <FontAwesomeIcon icon={faUsers} />
          Teams
        </div>
        <div className="flex flex-col align-items-start">
          {filteredTeams.map((team) => {
            const memberLength = team?.teamMemberIds?.length ?? 0;
            return getSelectItem(
              team?.teamId,
              "teamIds",
              team?.teamName ?? "",
              `${memberLength} member${getS(memberLength)}`,
              <AvatarCircle
                name={team?.teamName}
                avatarColor={team?.avatarColor}
                profilePicture={team?.profilePicture}
                size="small"
              />
            );
          })}
        </div>
      </>
    );
  };

  const getPeopleSelection = () => {
    if (filteredUsers.length === 0) {
      return null;
    }
    return (
      <>
        <div className={labelClassName}>
          <FontAwesomeIcon icon={faUser} />
          People
        </div>
        <div className="flex flex-col align-items-start">
          {filteredUsers.map((user) => {
            return getSelectItem(
              user.userAccountId,
              "userAccountIds",
              getUserName(user),
              user.jobTitle || "No job title",
              <AvatarCircle userAccountId={user.userAccountId} size="small" />
            );
          })}
        </div>
      </>
    );
  };

  const getDepartmentSelection = () => {
    if (filteredDepartments.length === 0) {
      return null;
    }
    return (
      <>
        <div className={labelClassName}>
          <FontAwesomeIcon icon={faHouseBlank} />
          Departments
        </div>
        <div className="flex flex-col align-items-start">
          {filteredDepartments.map((department) => {
            const totalDepartmentMembers = department?.teamMembers?.length ?? 0;
            return getSelectItem(
              department.departmentId,
              "departmentIds",
              department.name ?? "",
              `${totalDepartmentMembers} member${getS(totalDepartmentMembers)}`,
              <div className="avatar-circle initials border-white small">
                <FontAwesomeIcon icon={faHouseBlank} />
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const getEmptyState = () => {
    if (
      !filteredTeams.length &&
      !filteredUsers.length &&
      !filteredDepartments.length
    ) {
      return (
        <div className="d-flex justify-content-center align-items-center h-full">
          <p className="text-[var(--text-secondary)]">
            No results found matching "{searchInput}"
          </p>
        </div>
      );
    }
    return null;
  };

  // -------------------- Memoized values --------------------

  const totalEntitiesSelected = useMemo(() => {
    return Object.values(selectedEntitiesMaps).reduce((acc, entityMap) => {
      return acc + Object.values(entityMap).filter(Boolean).length;
    }, 0);
  }, [selectedEntitiesMaps]);

  const labelClassName =
    "text-[var(--text-label)] p-[12px_20px_4px] w-full d-flex gap-2 text-sm align-items-center";

  return (
    <Drawer
      direction="right"
      open={isOpen}
      onOpenChange={() => {
        onCloseDrawer();
      }}
    >
      <DrawerContent
        overlayClassName="bg-black/20"
        className={`right-0 top-0 left-auto bottom-auto mt-0 h-full ${GOAL_DRAWER_WIDTH} rounded-l-[10px] rounded-t-none figtree-container`}
      >
        <DrawerHeader className="d-flex justify-content-between align-items-center">
          <DrawerTitle>Share with people & groups</DrawerTitle>
          <DrawerDescription className="d-none">
            This drawer allows you to select entities to share a goal with.
          </DrawerDescription>
          <DrawerClose asChild>
            <Button variant="secondary-blue">
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <Separator />
        <div className="px-3 py-2">
          <DrawerSearchInput
            searchValue={searchInput}
            setSearchValue={setSearchInput}
          />
        </div>
        <Separator />
        <div className="no-scrollbar overflow-y-auto">
          {getTeamSelection()}
          {getDepartmentSelection()}
          {getPeopleSelection()}
        </div>
        {getEmptyState()}
        <Separator />
        <DrawerFooter className="d-flex flex-row justify-content-between align-items-center">
          <div>
            <p>{totalEntitiesSelected} selected</p>
          </div>
          <div className="d-flex flex-row gap-2">
            <DrawerClose asChild>
              <Button variant="secondary-gray">Cancel</Button>
            </DrawerClose>
            <Button onClick={onSaveHandler}>Submit</Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default EntitySelectorDrawer;
